/*
  Vendored from ZzFX v1.3.2 by Frank Force — https://github.com/KilledByAPixel/ZzFX (npm zzfx@1.3.2, ZzFX.js)
  Only `buildSamples` is kept (IMPLEMENTATION §1.1). Changes: TypeScript types; `this.sampleRate`/`this.volume`
  become arguments; `Math.random` is injected as `rand` so recipes can be rendered deterministically in tests.
  The synthesis maths is unchanged.

  ZzFX MIT License

  Copyright (c) 2019 - Frank Force

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in all
  copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
  SOFTWARE.
*/

/** The 21 ZzFX parameters, in order; missing entries take the ZzFX defaults. */
export type ZzfxParams = ReadonlyArray<number | undefined>;

export interface ZzfxContext {
  sampleRate: number;
  /** ZzFX master volume (upstream default .3). */
  volume: number;
  /** Uniform [0,1) source; upstream uses Math.random. */
  rand: () => number;
}

export function buildSamples(z: ZzfxContext, params: ZzfxParams): number[] {
  const p = (i: number, d: number): number => {
    const v = params[i];
    return v === undefined ? d : v;
  };
  let volume = p(0, 1);
  const randomness = p(1, 0.05);
  let frequency = p(2, 220);
  let attack = p(3, 0);
  let sustain = p(4, 0);
  let release = p(5, 0.1);
  const shape = p(6, 0);
  const shapeCurve = p(7, 1);
  let slide = p(8, 0);
  let deltaSlide = p(9, 0);
  let pitchJump = p(10, 0);
  let pitchJumpTime = p(11, 0);
  let repeatTime = p(12, 0);
  const noise = p(13, 0);
  let modulation = p(14, 0);
  const bitCrush = p(15, 0);
  let delay = p(16, 0);
  const sustainVolume = p(17, 1);
  let decay = p(18, 0);
  const tremolo = p(19, 0);
  const filter = p(20, 0);

  // init parameters
  const sampleRate = z.sampleRate;
  const PI2 = Math.PI * 2;
  const abs = Math.abs;
  const sign = (v: number): number => (v < 0 ? -1 : 1);
  slide *= (500 * PI2) / sampleRate / sampleRate;
  const startSlide = slide;
  frequency *= ((1 + randomness * 2 * z.rand() - randomness) * PI2) / sampleRate;
  let startFrequency = frequency;
  let modOffset = 0; // modulation offset
  let repeat = 0; // repeat offset
  let crush = 0; // bit crush offset
  let jump = 1; // pitch jump timer
  const b: number[] = []; // sample buffer
  let t = 0; // sample time
  let i = 0; // sample index
  let s = 0; // sample value
  let f: number; // wave frequency

  // biquad LP/HP filter
  const quality = 2;
  const w = (PI2 * abs(filter) * 2) / sampleRate;
  const cos = Math.cos(w);
  const alpha = Math.sin(w) / 2 / quality;
  const a0 = 1 + alpha;
  const a1 = (-2 * cos) / a0;
  const a2 = (1 - alpha) / a0;
  const b0 = (1 + sign(filter) * cos) / 2 / a0;
  const b1 = -(sign(filter) + cos) / a0;
  const b2 = b0;
  let x2 = 0;
  let x1 = 0;
  let y2 = 0;
  let y1 = 0;

  // scale by sample rate
  const minAttack = 9; // prevent pop if attack is 0
  attack = attack * sampleRate || minAttack;
  decay *= sampleRate;
  sustain *= sampleRate;
  release *= sampleRate;
  delay *= sampleRate;
  deltaSlide *= (500 * PI2) / sampleRate ** 3;
  modulation *= PI2 / sampleRate;
  pitchJump *= PI2 / sampleRate;
  pitchJumpTime *= sampleRate;
  repeatTime = (repeatTime * sampleRate) | 0;
  volume *= z.volume;

  // generate waveform
  const length = (attack + decay + sustain + release + delay) | 0;
  for (; i < length; b[i++] = s * volume) {
    if (!(++crush % ((bitCrush * 100) | 0))) {
      // bit crush
      s = shape
        ? shape > 1
          ? shape > 2
            ? shape > 3
              ? shape > 4
                ? ((t / PI2) % 1 < shapeCurve / 2 ? 1 : 0) * 2 - 1 // 5 square duty
                : Math.sin(t ** 3) // 4 noise
              : Math.max(Math.min(Math.tan(t), 1), -1) // 3 tan
            : 1 - ((((2 * t) / PI2) % 2) + 2) % 2 // 2 saw
          : 1 - 4 * abs(Math.round(t / PI2) - t / PI2) // 1 triangle
        : Math.sin(t); // 0 sin

      s =
        (repeatTime ? 1 - tremolo + tremolo * Math.sin((PI2 * i) / repeatTime) : 1) * // tremolo
        (shape > 4 ? s : sign(s) * abs(s) ** shapeCurve) * // shape curve
        (i < attack
          ? i / attack // attack
          : i < attack + decay // decay
            ? 1 - ((i - attack) / decay) * (1 - sustainVolume) // decay falloff
            : i < attack + decay + sustain // sustain
              ? sustainVolume // sustain volume
              : i < length - delay // release
                ? ((length - i - delay) / release) * sustainVolume // release falloff
                : 0); // post release

      s = delay
        ? s / 2 +
          (delay > i
            ? 0 // delay
            : ((i < length - delay ? 1 : (length - i) / delay) * // release delay
                (b[(i - delay) | 0] ?? 0)) /
              2 /
              volume) // sample delay
        : s;

      if (filter) {
        // apply filter
        const nx1 = s;
        const out = b2 * x2 + b1 * x1 + b0 * nx1 - a2 * y2 - a1 * y1;
        x2 = x1;
        x1 = nx1;
        y2 = y1;
        y1 = out;
        s = out;
      }
    }

    frequency += slide += deltaSlide;
    f = frequency * Math.cos(modulation * modOffset++); // frequency + modulation
    t += f + f * noise * Math.sin(i ** 5); // noise

    if (jump && ++jump > pitchJumpTime) {
      // pitch jump
      frequency += pitchJump; // apply pitch jump
      startFrequency += pitchJump; // also apply to start
      jump = 0; // stop pitch jump time
    }

    if (repeatTime && !(++repeat % repeatTime)) {
      // repeat
      frequency = startFrequency; // reset frequency
      slide = startSlide; // reset slide
      jump ||= 1; // reset pitch jump time
    }
  }

  return b;
}
